import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useSettings } from "../../lib/useSettings";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Loader2, Lock, ShieldCheck, Rocket, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint E — Page de connexion DÉDIÉE à l'Accès Agence.
 * Mode clair navy+sky+blanc. Accessible même sans is_hub=true (UX graceful).
 * Une fois loggé, redirige vers /agency.
 */
export default function AgencyLoginPage() {
  const { login, user } = useAuth();
  const { settings: s } = useSettings();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Déjà connecté → on renvoie sur /agency
  if (user && user.role === "admin") return <Navigate to="/agency/dashboard" replace />;

  const NAVY = s?.agency_navy_color || "#0F2744";
  const SKY = s?.agency_sky_color || "#38BDF8";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Email & mot de passe requis");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result && result.role && result.role !== "admin") {
        toast.error("Accès Agence réservé aux comptes administrateur");
        return;
      }
      toast.success("Bienvenue dans l'Accès Agence");
      // Sur cockpit.agence-msc.fr → /agency. Sur un site client → fallback /admin.
      nav(s?.is_hub === true ? "/agency/dashboard" : "/admin");
    } catch {
      toast.error("Identifiants invalides");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row"
      data-testid="agency-login-page"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY}F0 60%, ${NAVY}CC 100%)` }}
    >
      {/* Left — branding panel */}
      <div className="lg:w-1/2 p-8 lg:p-16 flex flex-col justify-between relative overflow-hidden">
        {/* Glow decoration */}
        <div className="absolute -top-40 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: SKY }} />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: SKY }} />

        <div className="relative z-10">
          <button
            type="button"
            onClick={() => nav("/")}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition mb-8"
            data-testid="agency-login-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>

          <div className="flex items-center gap-3 mb-12">
            {s?.agency_logo_url ? (
              <img src={s.agency_logo_url} alt={s?.agency_brand_name || "Agence"} className="w-14 h-14 rounded-xl object-cover bg-white" />
            ) : (
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: SKY }}>
                <Rocket className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: SKY }}>
                {s?.agency_tagline || "Accès Agence"}
              </div>
              <div className="text-2xl font-bold text-white">{s?.agency_brand_name || "Agence MSC"}</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-4">
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
            Pilote tous tes sites<br />depuis un seul cockpit.
          </h1>
          <p className="text-white/70 max-w-md">
            Forfaits, clients, onboarding, facturation, suspension automatique, thèmes IA, scraping de contenu —
            tout ce qu'il faut pour gérer ton parc d'instances COCKPIT.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {["Cockpit global", "Facturation auto", "Suspension J0/J+7", "Thèmes IA", "Onboarding Stripe"].map(t => (
              <span key={t} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs backdrop-blur-sm">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-white/40 text-xs mt-8">
          COCKPIT · v{s?.version || "1.3.0"} · {s?.agency_signature_email || s?.agency_email || "contact@agence-msc.fr"}
        </div>
      </div>

      {/* Right — form */}
      <div className="lg:w-1/2 p-6 lg:p-16 flex items-center justify-center">
        <Card className="w-full max-w-md p-8 bg-white shadow-xl">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${NAVY}10` }}>
              <ShieldCheck className="w-5 h-5" style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: SKY }}>
                Connexion sécurisée
              </div>
              <h2 className="text-xl font-bold" style={{ color: NAVY }}>Accès Agence</h2>
            </div>
          </div>

          <p className="text-sm text-slate-500 mb-6">
            Réservé à l'équipe <strong>{s?.agency_brand_name || "Agence MSC"}</strong>. Les identifiants client ne donnent pas accès à cet espace.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="agency-login-form">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="support@agence-msc.fr"
                  className="pl-9 h-11"
                  required
                  data-testid="agency-login-email"
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pl-9 h-11"
                  required
                  data-testid="agency-login-password"
                  autoComplete="current-password"
                  autoFocus
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 text-white"
              style={{ background: NAVY }}
              data-testid="agency-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {loading ? "Connexion…" : "Accéder à l'espace agence"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 text-xs text-slate-400 flex items-center gap-2">
            <Lock className="w-3 h-3" />
            Connexion chiffrée · Traçabilité des connexions · Sessions expirables
          </div>
        </Card>
      </div>
    </div>
  );
}
